import { useMemo } from 'react'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import type { CourseEquivalenceFormType } from '@/schemas/education/match-courses-schema'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import { useGroupedCoursesBySerie } from '../../hooks/use-grouped-courses-by-serie'
import { TranscriptTableRow } from '../transcript-table-row'

interface TranscriptTableProps {
  onlyView?: boolean
  desiredCourses?: DesiredEducationCourse[]
  courseEquivalences?: CourseEquivalenceFormType
  selectedSerie?: string
}

export function TranscriptTable({
  onlyView,
  desiredCourses,
  courseEquivalences,
  selectedSerie
}: TranscriptTableProps) {
  const { groupedCoursesBySerie } = useGroupedCoursesBySerie(
    desiredCourses ?? []
  )

  const equivalenceMap = useMemo(() => {
    if (onlyView || !courseEquivalences) return new Map()

    return new Map(
      courseEquivalences.map((equivalence) => [
        equivalence.desiredCourse.courseId,
        equivalence
      ])
    )
  }, [onlyView, courseEquivalences])

  return (
    <div className="flex flex-col gap-6">
      {Object.entries(groupedCoursesBySerie).map(([serie, courses]) => (
        <div
          key={serie}
          className="bg-background border rounded-t-xl rounded-b-xl pb-2 overflow-hidden"
        >
          <div className="w-full bg-background px-4 pt-3 pb-1 -mb-4 border-b font-medium text-[var(--brand-blue-2)] flex gap-3 text-base">
            Série:{' '}
            <span className="text-foreground">{serie.padStart(2, '0')}</span>
          </div>
          <Table className="[border-collapse:separate] [border-spacing:0_8px] [&>thead>tr]:border-spacing-0">
            <TableHeader className="translate-y-2">
              <TableRow>
                <TableHead className="px-4 bg-background font-semibold">
                  Disciplina
                </TableHead>
                <TableHead className="px-4 bg-background font-semibold w-40">
                  Carga Horária
                </TableHead>
                <TableHead className="px-4 bg-background font-semibold w-30">
                  Nota
                </TableHead>
                <TableHead className="px-4 bg-background font-semibold w-60">
                  Período
                </TableHead>
                <TableHead className="px-4 bg-background font-semibold w-90">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {courses.courses?.map((course, index) => (
                <TranscriptTableRow
                  key={course.courseId}
                  course={course}
                  equivalence={equivalenceMap.get(course.courseId)}
                  selectedSerie={selectedSerie}
                  index={index}
                />
              ))}
            </TableBody>
          </Table>
          <div className="w-full bg-background px-4 py-2 border-b -translate-y-2 text-foreground/30 font-bold">
            Total CH: {courses.totalWorkload}
          </div>
        </div>
      ))}
    </div>
  )
}
